// src/services/activityService.js
import apiClient from './api';
import logger from '../utils/logger';

class ActivityService {
  constructor() {
    this.intervalId = null;
    this.isActive = false;
    this.lastActivity = Date.now();
    this.eventListeners = [];
    this.startCount = 0;
    this.listenersSetup = false;
  }

  setupActivityListeners() {
    if (this.listenersSetup) {
      logger.warn('[ActivityService] Listeners already setup, skipping');
      return;
    }

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Use a single bound handler to prevent memory leaks
    const handleActivity = () => {
      this.lastActivity = Date.now();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
      // Store reference for cleanup
      this.eventListeners.push({ event, handler: handleActivity });
    });

    this.listenersSetup = true;
    logger.log('[ActivityService] Event listeners setup');
  }

  cleanup() {
    if (this.eventListeners.length === 0) {
      logger.warn('[ActivityService] No listeners to clean up');
      return;
    }

    // Remove all event listeners to prevent memory leaks
    this.eventListeners.forEach(({ event, handler }) => {
      document.removeEventListener(event, handler, true);
    });
    this.eventListeners = [];
    this.listenersSetup = false;
    logger.log('[ActivityService] Event listeners cleaned up');
  }

  start() {
    this.startCount++;

    if (this.isActive && this.intervalId) {
      logger.warn(`[ActivityService] Already running (start call #${this.startCount}), skipping`);
      return;
    }

    if (this.intervalId) {
      logger.warn(`[ActivityService] Stale interval detected (start call #${this.startCount}), clearing first`);
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Setup listeners if not already done
    this.setupActivityListeners();

    this.isActive = true;
    logger.log(`[ActivityService] Starting #${this.startCount}`);

    // Send immediate heartbeat
    this.sendHeartbeat();

    // Send heartbeat every 2 minutes if user is active
    this.intervalId = setInterval(() => {
      const timeSinceLastActivity = Date.now() - this.lastActivity;

      // Only send heartbeat if user was active in the last 5 minutes
      if (timeSinceLastActivity < 5 * 60 * 1000) {
        this.sendHeartbeat();
      }
    }, 2 * 60 * 1000); // Every 2 minutes
  }

  stop() {
    if (!this.isActive && !this.intervalId) {
      logger.warn('[ActivityService] Not running, nothing to stop');
      return;
    }

    this.isActive = false;
    logger.log(`[ActivityService] Stopping (had ${this.startCount} start calls)`);

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Clean up event listeners when stopping
    this.cleanup();
  }

  async sendHeartbeat() {
    try {
      await apiClient.post('/users/activity/');
      logger.log('[ActivityService] Heartbeat sent');
    } catch (error) {
      console.error('[ActivityService] Failed to send heartbeat:', error);
    }
  }
}

// Create singleton instance
const activityService = new ActivityService();

export default activityService;